import {useEffect, useMemo, useState} from "react";
import {awardToText, getCompetitionId, getTitles} from "../data/data";
import {useFilters, useGlobalFilter, usePagination, useSortBy, useTable} from "react-table";
import styled from "styled-components";
import SearchTable from "./SearchTable";
import {device} from "../theme";
import {useForm, FormProvider} from "react-hook-form";
import InputWithLabel from "./inputs/InputWithLabel";
import {Link, useLocation, useNavigate} from "react-router-dom";
import SelectStyled from "./inputs/SelectStyled";
import Select from "react-select";
import {useSearchParams} from "./hooks/useSearchParams";
import {Image} from "semantic-ui-react";
import {Menu} from "./layout/Menu";

const data = getTitles();

export const Search = () => {
    const methods = useForm();
    const {setParam, queryParams} = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const filterFields = useMemo(() => ["name", "short", "year", "award", "part", "club", "class", "theme"], [])

    useEffect(() => {
        resetAllFilters();

        queryParams.forEach((value, key) => {
            if (key === "search") {
                setGlobalFilter(value);
                methods.setValue("search", value);
                return;
            }
            if (!filterFields.includes(key)) return;
            setFilter(key, value || undefined);
        });
    }, [location]);

    function resetAllFilters() {
        setAllFilters([]);
    }

    const columns = useMemo(
        () => [
            {
                Header: "Bilde",
                accessor: "thumbnailUrl",
                disableFilters: true,
                Cell: ({value, row}) => (
                    value ? <Image src={value} alt="Bilde"/> : null
                )
            },
            {
                Header: "Navn",
                accessor: "name",
                Filter: SelectColumnFilter,
                filter: "exact"
            },
            {
                Header: "Tittel",
                accessor: "title",
                disableFilters: true,
            },
            {
                Header: "Klubb",
                accessor: "club",
                Filter: SelectColumnFilter,
                filter: "exact"
            },
            {
                Header: "Konkurranse",
                accessor: "short",
                Filter: SelectColumnFilter,
                filter: "exact",
            },
            {
                Header: "År",
                accessor: "year",
                Filter: SelectColumnFilter,
                filter: "exact",
            },
            {
                Header: "Del",
                accessor: "part",
                Filter: SelectColumnFilter,
                filter: "exact"
            },
            {
                Header: "Klasse",
                accessor: "class",
                Filter: SelectColumnFilter,
                filter: "exact"
            },
            {
                Header: "Tema",
                accessor: "theme",
                Filter: SelectColumnFilter,
                filter: "exact"
            },
            {
                Header: "Premie",
                accessor: "award",
                Filter: SelectColumnFilter,
                filter: "exact",
                Cell: ({value, row}) => {
                    if (value === "5" && row.values.short === "Klubb-NM") {
                        return "Utvalgt";
                    }
                    return awardToText[value];
                }
            },
            {
                Header: "Poeng",
                accessor: "score",
                disableFilters: true,
                showOnlyOnData: true
            }
        ], data);

    const search = methods.watch("search");

    const initialState = useMemo(() => {
        const filtersFromUrl = [...queryParams.entries()]
            .filter((entry) => filterFields.includes(entry[0]))
            .map((entry) => ({id: entry[0], value: entry[1] || undefined}));
        return {
            pageSize: 10,
            filters: filtersFromUrl,
            sortBy: [
                {
                    id: 'year',
                    desc: true
                },
                {
                    id: 'award',
                    asc: true
                }
            ]}
    }, []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,

        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        gotoPage,
        pageOptions,
        pageCount,

        setFilter,
        setAllFilters,
        setGlobalFilter,
        state: {pageIndex},
    } = useTable(
        {
            columns, 
            data, 
            initialState, 
            globalFilter: (rows, columns, filterValue) => {
                if (!filterValue) return rows;
                // Some titles store å as a + combining ring; compare in one Unicode form.
                const wanted = filterValue.normalize("NFC").toLowerCase();
                return rows.filter(row =>
                    row.values.title?.normalize("NFC").toLowerCase().includes(wanted)
                );
            },
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    );


    function SelectColumnFilter({column: {filterValue, preFilteredRows, id}}) {
        const options = useMemo(() => {
            const values = [...new Set(preFilteredRows.map((row) => row.values[id]))];
            let options;
            if (id==="award") {
                options = values
                    .filter((item) => !!item)
                    ?.map((item) => ({
                        value: item,
                        label: item,
                    }))
                    .sort((a, b) => (+a.value)-(+b.value)).map(item => {
                        // Check if this is award "5" and we have any Klubb-NM entries with this award
                        const hasKlubbNM = item.value === "5" && preFilteredRows.some(row =>
                            row.values.award === "5" && row.values.short === "Klubb-NM"
                        );
                        const hasOtherAward5 = item.value === "5" && preFilteredRows.some(row =>
                            row.values.award === "5" && row.values.short !== "Klubb-NM"
                        );

                        let label = awardToText[item.label];
                        if (item.value === "5") {
                            if (hasKlubbNM && hasOtherAward5) {
                                label = "Antatt/Utvalgt";
                            } else if (hasKlubbNM && !hasOtherAward5) {
                                label = "Utvalgt";
                            }
                        }

                        return {value: item.value, label: label};
                    })
            } else {
                options = values
                    .filter((item) => !!item)
                    ?.map((item) => ({
                        value: item,
                        label: item,
                    }))
                    .sort((a, b) => -a.label?.localeCompare(b.label));
            }
            return [{label: "Alle", value: ""}, ...options];
        }, [id, preFilteredRows]);
        const currentValue = options.find((item) => item.value === filterValue);

        return (
            <SelectStyled
                style={{minWidth: "4rem", marginTop: "0.2rem", marginBottom: "0.2rem"}}
            >
                <Select
                    defaultValue={options[0]}
                    value={currentValue || undefined}
                    onChange={(e) => setParam(id, e.value)}
                    options={options}
                    classNamePrefix="rs"
                    name={id}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                />
            </SelectStyled>
        );
    }

    useEffect(() => {
        if (search !== undefined) {
            setGlobalFilter(search)
            setParam("search", search)
        }
    }, [search]);

    return <>
        <Header>
            <div>
                <a href="https://nsff.no"><Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"}/></a>
            </div>
            <div>
                Konkurranser med NSFF godkjenning
            </div>
            <div>

            </div>
        </Header>
        <Menu/>
        <MainArea>
            <Container>
                <FormProvider {...methods}>
                    <SearchInput
                        name="search"
                        label={"Søk i tittel"}
                        autoComplete="off"
                    />
                    <SearchTable
                        getTableProps={getTableProps}
                        headerGroups={headerGroups}
                        getTableBodyProps={getTableBodyProps}
                        page={page}
                        prepareRow={prepareRow}
                        pageCount={pageCount}
                        previousPage={previousPage}
                        canPreviousPage={canPreviousPage}
                        nextPage={nextPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        gotoPage={gotoPage}
                        pageIndex={pageIndex}
                        isLoading={false}
                        rows={rows}
                    />
                </FormProvider>
            </Container>
        </MainArea>
    </>
}

const Container = styled.div`
  opacity: 0;
  animation: fadeIn 300ms ease forwards;
`;

const MainArea = styled.div`
  padding: 1.25rem 2.5rem 3.125rem 3.125rem;

  @media ${device.mobile} {
    padding: 1.25rem 0.625rem 3.125rem 0.625rem;
  }
`;

const SearchInput = styled(InputWithLabel)`
  flex-grow: 1;
  max-width: 27rem;
  margin-right: 1rem;
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;
  }

  > div:nth-child(3) {
    margin-right: 20px;
  }
`
